import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/toaster";
import Navbar from "./components/Navbar";
import WelcomePopup from "./components/WelcomePopup";
import ErrorBoundary from "./components/ErrorBoundary";
import errorLogger, { setErrorUser, clearErrorUser } from "./utils/errorLogger";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Colleges from "./pages/Colleges";
import MyColleges from "./pages/MyColleges";
import Application from "./pages/Application";
import CounselorPortal from "./pages/CounselorPortal";
import CollegeDemo from "./pages/CollegeDemo";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import DecisionTracker from "./pages/DecisionTracker";
import ParentPortal from "./pages/ParentPortal";
import ApplicationReview from "./pages/ApplicationReview";
import ScoreSending from "./pages/ScoreSending";
import FinancialAid from "./pages/FinancialAid";
import GradeReports from "./pages/GradeReports";
import DisciplinaryHistory from "./pages/DisciplinaryHistory";
import Scholarships from "./pages/Scholarships";
import CollegeCompare from "./pages/CollegeCompare";
import SubmissionConfirmation from "./pages/SubmissionConfirmation";
import CollegeMembership from "./pages/CollegeMembership";
import Contact from "./pages/Contact";

// Initialize error logging
errorLogger.init();
import Mission from "./pages/Mission";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import InterviewLanding from "./pages/interview/InterviewLanding";
import InterviewSession from "./pages/interview/InterviewSession";
import InterviewFeedback from "./pages/interview/InterviewFeedback";
import InterviewHistory from "./pages/interview/InterviewHistory";
import InterviewReplay from "./pages/interview/InterviewReplay";
import RealtimeInterview from "./pages/interview/RealtimeInterview";
import StructuredInterview from "./pages/interview/StructuredInterview";
import F1VisaGuide from "./pages/F1VisaGuide";
import AdmissionChances from "./pages/AdmissionChances";
// New public pages
import About from "./pages/About";
import Founders from "./pages/Founders";
import WhyJoinWayo from "./pages/WhyJoinWayo";
import HowItWorks from "./pages/HowItWorks";
import FAQ from "./pages/FAQ";
import WhyCollegeMatters from "./pages/WhyCollegeMatters";
import PayingForCollege from "./pages/PayingForCollege";
// University Hub pages
import UniversityLogin from "./pages/university/UniversityLogin";
import UniversityDashboard from "./pages/university/UniversityDashboard";
import UniversityApplications from "./pages/university/UniversityApplications";
import ApplicationReviewUniversity from "./pages/university/ApplicationReviewUniversity";
import UniversityAnalytics from "./pages/university/UniversityAnalytics";
import UniversityMessages from "./pages/university/UniversityMessages";
import StudentMessages from "./pages/StudentMessages";
import StudentAnalytics from "./pages/StudentAnalytics";
import AcademicAdvisor from "./pages/AcademicAdvisor";
import CareerSimulator from "./pages/CareerSimulator";
import Careers from "./pages/Careers";
import CareersAdmin from "./pages/CareersAdmin";
import UniversityRequest from "./pages/UniversityRequest";
import Pricing from "./pages/Pricing";
import RecommenderPortal from "./pages/RecommenderPortal";
import ErrorDashboard from "./pages/ErrorDashboard";
import DIDUsage from "./pages/admin/DIDUsage";
import EssayReview from "./pages/EssayReview";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  return !user ? children : <Navigate to="/dashboard" />;
};

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  
  // Set user context for error logging when user changes
  useEffect(() => {
    if (user) {
      setErrorUser(user._id || user.id, user.email);
    } else {
      clearErrorUser();
    }
  }, [user]);
  
  // Pages that should not show Navbar
  const hideNavbarPaths = ['/recommend'];
  const shouldHideNavbar = hideNavbarPaths.some(path => location.pathname.startsWith(path));

  return (
    <ErrorBoundary componentName="App">
      {!shouldHideNavbar && <Navbar />}
      <WelcomePopup />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/colleges" element={<PrivateRoute><Colleges /></PrivateRoute>} />
        <Route path="/my-colleges" element={<PrivateRoute><MyColleges /></PrivateRoute>} />
        <Route path="/application" element={<PrivateRoute><Application /></PrivateRoute>} />
        {/* AI Features - accessible without login, redirect to pricing when used */}
        <Route path="/interview" element={<InterviewLanding />} />
        <Route path="/interview/realtime" element={<RealtimeInterview />} />
        <Route path="/interview/structured" element={<StructuredInterview />} />
        <Route path="/interview/session/:sessionId" element={<PrivateRoute><InterviewSession /></PrivateRoute>} />
        <Route path="/interview/feedback/:sessionId" element={<PrivateRoute><InterviewFeedback /></PrivateRoute>} />
        <Route path="/interview/history" element={<PrivateRoute><InterviewHistory /></PrivateRoute>} />
        <Route path="/interview/replay/:sessionId" element={<PrivateRoute><InterviewReplay /></PrivateRoute>} />
        <Route path="/counselor" element={<CounselorPortal />} />
        <Route path="/demo" element={<CollegeDemo />} />
        <Route path="/payment/success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
        <Route path="/payment/cancel" element={<PrivateRoute><PaymentCancel /></PrivateRoute>} />
        <Route path="/decisions" element={<PrivateRoute><DecisionTracker /></PrivateRoute>} />
        <Route path="/parent" element={<ParentPortal />} />
        <Route path="/application/:applicationId/review" element={<PrivateRoute><ApplicationReview /></PrivateRoute>} />
        <Route path="/application/:applicationId/confirmation" element={<PrivateRoute><SubmissionConfirmation /></PrivateRoute>} />
        <Route path="/scores" element={<PrivateRoute><ScoreSending /></PrivateRoute>} />
        <Route path="/financial-aid" element={<PrivateRoute><FinancialAid /></PrivateRoute>} />
        <Route path="/grade-reports" element={<PrivateRoute><GradeReports /></PrivateRoute>} />
        <Route path="/disciplinary" element={<PrivateRoute><DisciplinaryHistory /></PrivateRoute>} />
        <Route path="/scholarships" element={<PrivateRoute><Scholarships /></PrivateRoute>} />
        <Route path="/compare" element={<PrivateRoute><CollegeCompare /></PrivateRoute>} />
        <Route path="/join" element={<CollegeMembership />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/mission" element={<Mission />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/f1-guide" element={<F1VisaGuide />} />
        {/* AI Features - accessible without login, redirect to pricing when used */}
        <Route path="/admission-chances" element={<AdmissionChances />} />
        {/* New public pages */}
        <Route path="/about" element={<About />} />
        <Route path="/about/founders" element={<Founders />} />
        <Route path="/about/mission" element={<Mission />} />
        <Route path="/why-join" element={<WhyJoinWayo />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/academic-advisor" element={<AcademicAdvisor />} />
        <Route path="/career-simulator" element={<CareerSimulator />} />
        <Route path="/essay-review" element={<EssayReview />} />
        <Route path="/why-college-matters" element={<WhyCollegeMatters />} />
        <Route path="/paying-for-college" element={<PayingForCollege />} />
        {/* University Hub */}
        <Route path="/university/login" element={<UniversityLogin />} />
        <Route path="/university/dashboard" element={<UniversityDashboard />} />
        <Route path="/university/applications" element={<UniversityApplications />} />
        <Route path="/university/applications/:applicationId" element={<ApplicationReviewUniversity />} />
        <Route path="/university/analytics" element={<UniversityAnalytics />} />
        <Route path="/university/messages" element={<UniversityMessages />} />
        {/* Student Messages */}
        <Route path="/messages" element={<PrivateRoute><StudentMessages /></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute><StudentAnalytics /></PrivateRoute>} />
        <Route path="/university-request" element={<UniversityRequest />} />
        {/* Careers */}
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:jobId" element={<Careers />} />
        <Route path="/careers/admin" element={<CareersAdmin />} />
        {/* Pricing */}
        <Route path="/pricing" element={<Pricing />} />
        {/* Recommender Portal - Public, no auth */}
        <Route path="/recommend/:token" element={<RecommenderPortal />} />
        {/* Error Dashboard - Admin only */}
        <Route path="/admin/errors" element={<ErrorDashboard />} />
        <Route path="/admin/did-usage" element={<PrivateRoute><DIDUsage /></PrivateRoute>} />
      </Routes>
      <Toaster />
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
